/* Pure catalog search and paging; no DOM, image loading or external requests. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.SeeGartProductPicker = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  const synonyms = Object.freeze({
    '黑色': ['black', 'charcoal'],
    '白色': ['white', 'ivory', 'cream'],
    '米色': ['beige', 'taupe', 'cream', 'sand'],
    '蓝色': ['blue', 'navy'], '藍色': ['blue', 'navy'],
    '绿色': ['green', 'sage', 'olive'], '綠色': ['green', 'sage', 'olive'],
    '海洋': ['ocean', 'sea', 'seascape', 'coastal'],
    '风景': ['landscape', 'scenery', 'horizon'], '風景': ['landscape', 'scenery', 'horizon'],
    '几何': ['geometric', 'geometry'], '幾何': ['geometric', 'geometry'],
    '人物': ['portrait', 'figure', 'figurative', 'woman', 'man', 'girl', 'boy', 'people'],
    '肌理': ['textured', 'texture', 'impasto', 'relief'],
    '抽象': ['abstract']
  });
  const chineseTerms = new RegExp(Object.keys(synonyms).join('|'), 'g');
  const list = value => Array.isArray(value) ? value : [];
  function normalize(value) {
    return String(value == null ? '' : value).normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
      // SJ#0332, SJ-0332, SJ 0332 and SJ0332 identify the same artwork code.
      .replace(/\bsj[\s\p{P}\p{S}]*([0-9]+)\b/gu, 'sj$1')
      .replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  }
  function identity(value) {
    const text = String(value == null ? '' : value).trim();
    const match = /^(?:gid:\/\/shopify\/(?:Product|Collection)\/)?(\d+)$/.exec(text);
    return match ? match[1] : text;
  }
  function words(product) {
    // Deliberately exclude finish options: a black frame does not make every
    // artwork a match for a search for black paintings.
    const skuValues = [product.sku, ...list(product.skus), ...list(product.variants).map(v => v && v.sku)].filter(v => v != null);
    const title = normalize(product.title);
    const titleHasCode = title.split(' ').some(word => /^sj\d+$/.test(word));
    // Shopify handles can retain an earlier artwork code after a title rename.
    // Do not let that legacy code select a different currently named artwork.
    const handle = normalize(product.handle).split(' ').filter(word => !titleHasCode || !/^sj\d+$/.test(word)).join(' ');
    const text = normalize([title, handle, ...skuValues].filter(v => v != null).join(' '));
    const result = new Set(text ? text.split(' ') : []);
    [product.id, product.numericId].forEach(id => { const key = identity(id); if (key) result.add(normalize(key)); });
    for (const word of [...result]) if (/^sj\d+$/.test(word)) result.add(word.slice(2));
    return [...result];
  }
  function termsFor(value) {
    // Segment recognized Chinese terms even when no spaces were entered.
    const normalizedQuery = normalize(String(value == null ? '' : value).replace(chineseTerms, term => ` ${term} `));
    const tokens = [...new Set(normalizedQuery ? normalizedQuery.split(' ') : [])];
    return { normalizedQuery, tokens, terms: tokens.map(token => ({ token, alternatives: synonyms[token] || [token] })) };
  }
  function matchesTerm(haystack, term) {
    return term.alternatives.some(needle => haystack.some(word => {
      // Exact numeric identifiers prevent a complete SJ0332 search from also
      // returning SJ03320. Ordinary words support incremental prefix typing.
      if (/^(?:sj)?\d+$/.test(needle)) return word === needle;
      // Translated meanings use whole words, so 海洋 (sea) does not match
      // "season" and 人物 (man) does not match "many".
      if (synonyms[term.token]) return word === needle || word === needle + 's' || word === needle + 'es';
      return word.startsWith(needle);
    }));
  }
  function query(products, collections, options) {
    const items = list(products), categories = list(collections), settings = options || {};
    const parsed = termsFor(settings.query);
    const requestedCollection = settings.collection == null || settings.collection === '' ? 'all' : String(settings.collection);
    let allowed = null, excluded = new Set(), collectionFound = requestedCollection === 'all';
    if (!collectionFound) {
      const category = categories.find(c => c && (String(c.handle) === requestedCollection || identity(c.id) === identity(requestedCollection) || identity(c.shopifyId) === identity(requestedCollection)));
      collectionFound = Boolean(category);
      allowed = new Set(category ? list(category.productIds).map(identity) : []);
      excluded = new Set(category ? list(category.historicalExtraProductIds).map(identity) : []);
    }
    const matches = [];
    items.forEach((product, index) => {
      if (!product || typeof product !== 'object') return;
      const id = identity(product.id || product.numericId);
      if (allowed && (!allowed.has(id) || excluded.has(id))) return;
      const searchWords = parsed.terms.length ? words(product) : [];
      if (!parsed.terms.every(term => matchesTerm(searchWords, term))) return;
      matches.push({ product, index });
    });
    const requestedLimit = settings.limit === undefined ? 12 : Number(settings.limit);
    const limit = Number.isFinite(requestedLimit) ? Math.max(0, Math.floor(requestedLimit)) : 12;
    const visible = matches.slice(0, limit);
    return { matches, visible, total: matches.length, limit, shown: visible.length, hasMore: visible.length < matches.length,
      collection: requestedCollection, collectionFound, normalizedQuery: parsed.normalizedQuery, tokens: parsed.tokens };
  }
  return Object.freeze({ query, normalize });
});
